import {createFileRoute} from "@tanstack/react-router";
import {z as zod} from "zod";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {ErrorComponent} from "./-errorComponent";
import {NotFoundComponent} from "./-notFoundComponent";
import {PendingComponent} from "./-pendingComponent";
import {RouteComponent} from "./-routeComponent";
import {reportMetadataZodValidator, type ReportMetadata} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/reportsWorkspaceCapability/domain/Report";
import type {ImageDetails} from "@vesta/janus-compatibility-module/utilities/typeDefinitions";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {fetchDatasets} from "~/utilities/reports/reportsAnalyticsApi";
import type {DatasetMetadata} from "~/routes/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/a/_adminScaffold/debugger/-types";
import {ADGPT__DATASETS__QUERY_KEY} from "~/utilities/queryKeys";

export const Route = createFileRoute(
    "/_withAuthenticationStatus/_authenticated/_withProfileStatus/_profileCompleted/w/$workspaceId/_defaultScaffold/favourite-reports",
)({
    loader: async ({context}) => {
        const authorizedReportsResult = await robustJsonResultClientFetch<Array<ReportMetadata>>(
            `${context.intellsysBaseUrl}/sub-system/pluto/reports/get`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${context.authenticationStatus.accessToken}`,
                    "X-Workspace-Id": context.workspace.id,
                },
                body: JSON.stringify({
                    filter: {
                        isFavorite: true,
                    }
                }),
            },
            zod.array(reportMetadataZodValidator),
        );
        if (authorizedReportsResult.success === false) {
            throw authorizedReportsResult.error;
        }
        const authorizedReports = authorizedReportsResult.data;

        const datasets = await context.queryClient.ensureQueryData<Array<DatasetMetadata>>({
            queryKey: [ADGPT__DATASETS__QUERY_KEY, context.workspace.id],
            queryFn: async () => {
                const result = await fetchDatasets({
                    intellsysBaseUrl: context.intellsysBaseUrl,
                    accessToken: context.authenticationStatus.accessToken,
                    workspaceId: context.workspace.id,
                });
                if (result.success === false) {
                    throw result.error;
                }
                return result.data;
            },
            retry: false,
            staleTime: Infinity,
        });

        const datasetLogoMap: Map<Uuid, ImageDetails | null> = new Map();
        for (const dataset of datasets) {
            datasetLogoMap.set(dataset.id, dataset.imageDetails);
        }

        return {
            workspace: context.workspace,
            authorizedReports,
            datasetLogoMap: datasetLogoMap,
            intellsysBaseUrl: context.intellsysBaseUrl,
            accessToken: context.authenticationStatus.accessToken,
        };
    },
    component: RouteComponent,
    errorComponent: ErrorComponent,
    pendingComponent: PendingComponent,
    notFoundComponent: NotFoundComponent,
});
